/**
 * /wireline-slickline/lines/new — add a line (POST /wireline-slickline/lines,
 * fieldiq-api#1336). A full page, not a modal. On save the new line's own page
 * replaces this one; spooling it onto a drum is its first event, logged there.
 *
 * WL_GEAR_WRITE only (admin, office_staff, supervisor). A 409
 * duplicate_line_number shows the API's sentence and links to that line.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useCreateWlLine, useWlMeta } from '../../hooks/useWirelineSlickline';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canWriteWlGear, existingIdOf, wlRefusal } from '../../lib/wireline-slickline';
import { useBackToList } from '../downhole/recordPage';
import { LineForm } from './LineForm';
import { WL_EYEBROW, WlEmpty, WlLoading, WlPanel } from './WlBits';

export default function WlNewLineScreen() {
  const navigate = useNavigate();
  const back = useBackToList('/wireline-slickline/lines');
  const { user } = useAuth();
  const canAdd = canWriteWlGear(user?.role);
  const meta = useWlMeta();
  const create = useCreateWlLine();
  const [error, setError] = useState<string | null>(null);
  const [duplicateOf, setDuplicateOf] = useState<string | null>(null);
  const failure = queryLoadFailure(meta);

  return (
    <div data-testid="wl-new-line-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand eyebrow={WL_EYEBROW} title="New line" onBack={back} backLabel="Back" />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <WlPanel eyebrow="Line register" title="Line">
            {!canAdd ? (
              <WlEmpty>Only an admin, office staff or a supervisor can add a line.</WlEmpty>
            ) : meta.isLoading ? (
              <WlLoading label="Loading form" />
            ) : failure ? (
              <LoadFailureNotice error={queryFailureError(meta)} kind={failure} subject="the line form" />
            ) : (
              <LineForm
                line={null}
                catalog={meta.data?.data ?? null}
                meta={meta.data?.meta ?? null}
                pending={create.isPending}
                error={error}
                duplicateOf={duplicateOf}
                submitLabel="Create line"
                onCancel={back}
                onSubmit={(body) => {
                  setError(null);
                  setDuplicateOf(null);
                  create.mutate(body, {
                    onSuccess: (res) => {
                      toast.success('Line created');
                      if (res.data?.id) navigate(`/wireline-slickline/lines/${res.data.id}`, { replace: true });
                      else navigate('/wireline-slickline/lines', { replace: true });
                    },
                    onError: (err) => {
                      setError(wlRefusal(err, 'The line could not be created.'));
                      setDuplicateOf(existingIdOf(err, 'existingLineId'));
                    },
                  });
                }}
              />
            )}
          </WlPanel>
        </div>
      </section>
    </div>
  );
}
